import React, { useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import UserBadgeTag from "../shared/UserBadgeTag";
import TopupPackageTag from "../shared/TopupPackageTag";
import { Table, Button, message, Tooltip } from "antd";

import { CopyOutlined } from "@ant-design/icons";

import { formatDateTime, getRelativeTimeFromNow } from "../../helpers/date";

import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const DASHBOARD_URL =
  process.env.REACT_APP_DASHBOARD_URL || "https://partner.parceldaily.com";
const AccountManagerList = ({
  accountManager,
  selectedRowKeys,
  onSelectRows,
  name,
  loading
}) => {

  const { permissions } = usePermissions();
  const table = [
    {
      title: "Id",
      dataIndex: "accountManagerId",
      key: "accountManagerId",
      width: 120,
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      width: 230,
      sorter: {
        compare: (a, b) => a.username.length - b.username.length,
        multiple: 1,
      },
      sortDirections: ["descend", "ascend"],
      render: (text, row) =>
        permissions?.includes(PERMISSIONS.VIEW_ACCOUNT_MANAGER_DETAILS) ? (
          <Link to={`/account-manager/${row.id}`}>{text}</Link>
        ) : (
          text
        ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 230,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 150,
    },
    {
      title: "Total Topup Performance (RM)",
      dataIndex: "topupPerformance",
      key: "topupPerformance",
    },
    {
      title: "Total Topup Count",
      dataIndex: "topupCount",
      key: "topupCount",
      width: 100,
    },
    {
      title: "New User Count",
      dataIndex: ["newUserStats", "merchantCount"], // Accessing nested property
      key: "merchantCount",
      width: 100,
      render: (text, record) => (
        <span>{record.newUserStats ? record.newUserStats.merchantCount : "-"}</span>
      ),
    },
    {
      title: "New User Topup Performance (RM)",
      dataIndex: ["newUserStats", "totalTopup"], // Accessing nested property
      key: "totalTopup",
      render: (text, record) => (
        <span>{record.newUserStats ? record.newUserStats.totalTopup : "-"}</span>
      ),
    },
    {
      title: "New User Total Topup Count",
      dataIndex: ["newUserStats", "topupMerchantCount"], // Accessing nested property
      key: "topupMerchantCount",
      render: (text, record) => (
        <span>{record.newUserStats ? record.newUserStats.topupMerchantCount : "-"}</span>
      ),
    },
    {
      title: "Register URL",
      dataIndex: "accountManagerId",
      width: 400,
      render: (text, row) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <span style={{ alignSelf: "flex-start", marginRight: 8, overflow: "hidden", whiteSpace: "none", textOverflow: "ellipsis" }}>
            {`${DASHBOARD_URL}/auth/sign-up?accountManagerId=${row.accountManagerId}`}
          </span>
          <Button
            style={{ width: 32, flex: "none" }}
            icon={<CopyOutlined />}
            onClick={() => {
              navigator.clipboard.writeText(
                `${DASHBOARD_URL}/auth/sign-up?accountManagerId=${row.accountManagerId}`
              );
              message.success("Copied to clipboard");
            }}
          />
        </div>
      ),
    },
  ];

  const userTable = [
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      width: 230,
      sorter: {
        compare: (a, b) => a.username.length - b.username.length,
        multiple: 1,
      },
      sortDirections: ["descend", "ascend"],
      render: (text, row) => <a href={`/user/${row.id}`}>{text}</a>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 230,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Credit",
      dataIndex: "credit",
      key: "credit",
      sorter: {
        compare: (a, b) => a.credit - b.credit,
        multiple: 2,
      },
    },
    {
      title: "Total Topup",
      dataIndex: "totalTopup",
      key: "totalTopup",
      sorter: {
        compare: (a, b) => a.totalTopup - b.totalTopup,
        multiple: 3,
      },
    },
    {
      title: "Package",
      dataIndex: "topupPackage",
      key: "topupPackage",
      render: (text) => (text ? <TopupPackageTag packageKey={text} /> : "-"),
    },
    {
      title: "Badge",
      dataIndex: "badge",
      key: "badge",
      render: (text, row) =>
        text ? <UserBadgeTag statusKey={text}>Verified</UserBadgeTag> : "-",
    },
    {
      title: "Total Usage",
      dataIndex: "totalUsage",
      key: "totalUsage",
      render: (text) => (text ? text.toFixed(2) : "-"),
      sorter: {
        compare: (a, b) => a.totalUsage - b.totalUsage,
        multiple: 2,
      },
    },
    {
      title: "Total Order",
      dataIndex: "totalNoOrder",
      key: "totalNoOrder",
      sorter: {
        compare: (a, b) => a.totalTopup - b.totalTopup,
        multiple: 3,
      },
    },
    {
      title: "Last Active Date",
      dataIndex: "lastDateActive",
      key: "lastDateActive",
      render: (text) => (text === null || text === undefined ? "-" : <Tooltip placement="topLeft" title={formatDateTime(text)}>
        {getRelativeTimeFromNow(text)}
      </Tooltip>),
    },
    {
      title: "Referral Code",
      dataIndex: "referralCode",
      key: "referralCode",
      render: (text) => (text ? text : "-"),
      sorter: (a, b) => {
        a = a.referralCode || "";
        b = b.referralCode || "";
        return a.localeCompare(b);
      },
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Locked Tier",
      dataIndex: "lockedTier",
      key: "lockedTier",
    },
    {
      title: "Registered Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => (text ? formatDateTime(text) : "-"),
      sorter: {
        compare: (a, b) => a.totalTopup - b.totalTopup,
        multiple: 3,
      },
    },
  ];

  return (
    <div className="table">
      {name === "user" ? (
        <Table
          rowKey="objectId"
          rowSelection={{ selectedRowKeys, onChange: onSelectRows }}
          className="table-section"
          columns={userTable}
          dataSource={accountManager}
          pagination={false}
          scroll={{ x: 1450, y: 500 }}
          loading={loading}
        />
      ) : (
        <Table
          rowKey="objectId"
          rowSelection={{ selectedRowKeys, onChange: onSelectRows }}
          className="table-section"
          columns={table}
          dataSource={accountManager}
          pagination={false}
          scroll={{ x: 1800, y: 500 }}
          loading={loading}
        />
      )}
    </div>
  );
};

export default AccountManagerList;
